import { useMemo, useState } from 'react';

import { useApproveIndustry, useIndustries, useSaveIndustry } from '@/api/queries';
import type { Industry } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { useClientPaging } from '@/ui/useClientPaging';
import { nextSort, statusOf, visibleIndustries, type Sort, type SortKey, type Status } from './industryList';

export type Dialog = { kind: 'add' } | { kind: 'edit'; industry: Industry } | { kind: 'reject'; industry: Industry };

function usePermissions() {
  const { can } = useSession();
  const canUpdate = can('industry.update');
  const canReview = can('industry.review');
  return { canCreate: can('industry.create'), canUpdate, canReview, withActions: canUpdate || canReview };
}

/** State, data and permissions of the Industries page. */
export function useIndustriesPage() {
  const permissions = usePermissions();
  const industries = useIndustries();
  const save = useSaveIndustry();
  const approve = useApproveIndustry();

  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [sort, setSort] = useState<Sort>({ key: 'name', order: 'asc' });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [approving, setApproving] = useState<Industry | null>(null);

  const rows = useMemo(() => industries.data?.industries ?? [], [industries.data]);
  const choices = rows.filter((i) => statusOf(i) === 'active');
  const countOf = (status: Status) => rows.filter((i) => statusOf(i) === status).length;

  const visible = useMemo(() => visibleIndustries(rows, search, statuses, sort), [rows, search, statuses, sort]);
  const { page, setPage, pageSize, setPageSize, pageRows } = useClientPaging(visible, [search, statuses, sort]);

  const onSort = (key: string) => setSort((s) => nextSort(s, key as SortKey));

  return {
    ...permissions,
    industries,
    save,
    approve,
    search,
    setSearch,
    statuses,
    setStatuses,
    sort,
    onSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    dialog,
    setDialog,
    approving,
    setApproving,
    rows,
    choices,
    countOf,
    visible,
    pageRows,
    mutationError: (!dialog && (save.error ?? null)) || null,
  };
}

export type IndustriesPageState = ReturnType<typeof useIndustriesPage>;
